import api from './api';
import { Content, ContentGeneration, ContentType, ContentTone, ApiResponse, PaginatedResponse } from '../types';

interface ContentFilters {
  page?: number;
  limit?: number;
  type?: ContentType;
  status?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

interface GenerateContentParams {
  type: ContentType | 'outline';
  prompt: string;
  parameters?: {
    tone?: ContentTone;
    length?: 'short' | 'medium' | 'long';
    keywords?: string[];
    target_audience?: string;
    language?: string;
  };
}

export const contentService = {
  async getContents(filters?: ContentFilters): Promise<PaginatedResponse<Content>> {
    const response = await api.get('/content', { params: filters });
    return response.data;
  },

  async getContent(id: string): Promise<ApiResponse<Content>> {
    const response = await api.get(`/content/${id}`);
    return response.data;
  },

  async createContent(data: Partial<Content>): Promise<ApiResponse<Content>> {
    const response = await api.post('/content', data);
    return response.data;
  },

  async updateContent(id: string, data: Partial<Content>): Promise<ApiResponse<Content>> {
    const response = await api.put(`/content/${id}`, data);
    return response.data;
  },

  async deleteContent(id: string): Promise<ApiResponse<void>> {
    const response = await api.delete(`/content/${id}`);
    return response.data;
  },

  async generateContent(params: GenerateContentParams & { async?: boolean }): Promise<ApiResponse<{ id: string; content: string; tokensUsed: number }> & { async?: boolean; jobId?: string; generationId?: string }> {
    const response = await api.post('/content/generate', params);
    return response.data;
  },

  async improveContent(id: string, instruction: string): Promise<ApiResponse<{ id: string; content: string; tokensUsed: number }>> {
    const response = await api.post(`/content/${id}/improve`, { instruction });
    return response.data;
  },

  async getGenerations(filters?: { page?: number; limit?: number; type?: string; status?: string }): Promise<PaginatedResponse<ContentGeneration>> {
    const response = await api.get('/content/generations', { params: filters });
    return response.data;
  },

  // AI Suggestions
  async getSuggestions(limit?: number): Promise<ApiResponse<any[]>> {
    const response = await api.get('/content/suggestions/list', { params: { limit } });
    return response.data;
  },

  async generateSuggestions(count?: number, useAsync = true): Promise<ApiResponse<any[]> & { async?: boolean; jobId?: string }> {
    const response = await api.post('/content/suggestions/generate', { count, async: useAsync });
    return response.data;
  },

  async acceptSuggestion(id: string, scheduledAt?: string): Promise<ApiResponse<any>> {
    const response = await api.post(`/content/suggestions/${id}/accept`, { scheduled_at: scheduledAt });
    return response.data;
  },

  async regenerateSuggestion(id: string, useAsync = true): Promise<ApiResponse<any> & { async?: boolean; jobId?: string }> {
    const response = await api.post(`/content/suggestions/${id}/regenerate`, { async: useAsync });
    return response.data;
  },

  async discardSuggestion(id: string): Promise<ApiResponse<void>> {
    const response = await api.delete(`/content/suggestions/${id}`);
    return response.data;
  },

  async getHashtagSuggestions(content: string, platform?: string): Promise<ApiResponse<string[]>> {
    const response = await api.post('/content/suggestions/hashtags', { content, platform });
    return response.data;
  },

  async getBestPostingTime(platform?: string): Promise<ApiResponse<{ suggested_time: string; platform: string }>> {
    const response = await api.get('/content/suggestions/best-time', { params: { platform } });
    return response.data;
  },

  async generateImage(prompt: string, style?: string, size?: string, useAsync = true): Promise<ApiResponse<{ url: string; prompt: string; style: string }> & { async?: boolean; jobId?: string }> {
    const response = await api.post('/content/generate-image', { prompt, style, size, async: useAsync });
    return response.data;
  },

  async generateVideo(prompt: string, style?: string, aspectRatio?: string): Promise<ApiResponse<{ url: string; prompt: string; style: string; thumbnail?: string }>> {
    const response = await api.post('/content/generate-video', { prompt, style, aspect_ratio: aspectRatio });
    return response.data;
  }
};
